import { useCallback, useEffect, useState } from 'react';

export const useCarousel = (
  ref: HTMLDivElement | null,
  content: unknown, // Not for reading, just for comparing content updates
  PADDING: number,
  IS_ONSCREEN_THRESHOLD: number
) => {
  const [numElementsLeft, setNumElementsLeft] = useState<number>();
  const [numElementsRight, setNumElementsRight] = useState<number>();

  useEffect(() => {
    if (!ref) {
      return;
    }

    const rect = ref.getBoundingClientRect();

    const children = Array.from(ref.querySelectorAll<HTMLElement>(':scope > *'));

    const update = () => {
      // This function responds to a scroll event listener and may be executed basically continuously
      //
      // Array.foreach() iterates n function calls which, in such a tight execution loop, can actually
      // cause noticeable lag to the user.
      //
      // As of 8/24/2022, using Array.foreach() instead of a for loop causes the segment divider to
      // freeze and then jump positions rather than moving smoothly

      // eslint-disable-next-line functional/no-let -- Transient counter vars
      let [$numElementsLeft, $numElementsRight] = [0, 0];

      // eslint-disable-next-line functional/no-loop-statement -- ^^^
      for (const child of children) {
        const childRect = child.getBoundingClientRect();
        const visibleWidth = Math.min(rect.right, childRect.right) - Math.max(rect.left, childRect.left);

        if (visibleWidth <= IS_ONSCREEN_THRESHOLD) {
          child.classList.add('offscreen');
        } else {
          child.classList.remove('offscreen');
        }

        // If _any part_ of child element is out of bounds
        if (childRect.left < rect.left) $numElementsLeft++;
        if (childRect.right > rect.right) $numElementsRight++;
      }

      setNumElementsLeft($numElementsLeft);
      setNumElementsRight($numElementsRight);
    };

    update();
    ref.addEventListener('scroll', update, { passive: true });

    return () => ref.removeEventListener('scroll', update);
  }, [
    ref,
    content, // Note: Pass in items to re-evaluate carousel contents on change (example: filters result in new items)
    IS_ONSCREEN_THRESHOLD,
  ]);

  const scrollLeft = useCallback(() => ref?.scrollBy({ left: -ref.clientWidth, behavior: 'smooth' }), [ref]);
  const scrollRight = useCallback(() => ref?.scrollBy({ left: ref.clientWidth, behavior: 'smooth' }), [ref]);

  return {
    scrollLeft,
    scrollRight,
    hasOverflow: !!(numElementsLeft && numElementsLeft > 0) || !!(numElementsRight && numElementsRight > 0),
    numElementsLeft,
    numElementsRight,
  };
};
